/**
 * Minimal toast primitive. Lives at the app shell level — every
 * authenticated route can pushToast() from inside an effect or event
 * handler. The viewport renders fixed-position cards in the lower-right.
 *
 * Intentionally small — we may swap to a richer library later, but the
 * useToast() API is what call sites depend on and is stable.
 */

import {
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useMemo,
  useState,
} from "react";

export type ToastKind = "error" | "success" | "info";

export type Toast = {
  id: string;
  kind: ToastKind;
  title: string;
  /** Optional secondary line (e.g. request id, retry suggestion). */
  body?: string;
};

type ToastInput = Omit<Toast, "id"> & { id?: string };

export type ToastContextValue = {
  toasts: ReadonlyArray<Toast>;
  pushToast: (input: ToastInput) => string;
  dismissToast: (id: string) => void;
};

const ToastContext = createContext<ToastContextValue | null>(null);

const DEFAULT_TTL_MS = 6_000;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ReadonlyArray<Toast>>([]);
  const baseId = useId();

  const pushToast = useCallback(
    (input: ToastInput): string => {
      const id = input.id ?? `${baseId}-${crypto.randomUUID()}`;
      const toast: Toast =
        input.body !== undefined
          ? { id, kind: input.kind, title: input.title, body: input.body }
          : { id, kind: input.kind, title: input.title };
      setToasts((prev) => [...prev, toast]);
      return id;
    },
    [baseId],
  );

  const dismissToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const value = useMemo(
    () => ({ toasts, pushToast, dismissToast }),
    [toasts, pushToast, dismissToast],
  );

  return (
    <ToastContext.Provider value={value}>
      {children}
      <ToastViewport toasts={toasts} dismiss={dismissToast} />
    </ToastContext.Provider>
  );
}

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error("useToast must be called inside <ToastProvider />");
  return ctx;
}

function ToastViewport({
  toasts,
  dismiss,
}: {
  toasts: ReadonlyArray<Toast>;
  dismiss: (id: string) => void;
}) {
  return (
    <div
      aria-live="polite"
      aria-atomic="true"
      className="pointer-events-none fixed inset-x-0 bottom-6 z-50 flex flex-col items-end gap-2 px-6"
    >
      {toasts.map((t) => (
        <ToastCard key={t.id} toast={t} dismiss={dismiss} />
      ))}
    </div>
  );
}

function ToastCard({
  toast,
  dismiss,
}: {
  toast: Toast;
  dismiss: (id: string) => void;
}) {
  useEffect(() => {
    const timer = window.setTimeout(() => dismiss(toast.id), DEFAULT_TTL_MS);
    return () => window.clearTimeout(timer);
  }, [toast.id, dismiss]);

  const accent =
    toast.kind === "error"
      ? "border-destructive/40 bg-destructive/10"
      : toast.kind === "success"
        ? "border-foreground/25 bg-popover"
        : "border-border bg-popover";

  return (
    <output
      className={`pointer-events-auto block max-w-sm rounded-md border ${accent} px-4 py-3 text-sm text-foreground shadow-lg backdrop-blur`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="font-medium">{toast.title}</div>
          {toast.body && <div className="mt-1 text-xs text-muted-foreground">{toast.body}</div>}
        </div>
        <button
          type="button"
          onClick={() => dismiss(toast.id)}
          className="-mt-1 -mr-1 rounded p-1 text-muted-foreground transition hover:text-foreground"
          aria-label="Dismiss"
        >
          ×
        </button>
      </div>
    </output>
  );
}
